/**
 * ProfileLanding — the `/` route's profile-derived resolution
 * (CC-V35-BANK-PROFILE-01; accepted design §5.6/§5.10; D-098 tranche 4;
 * D-099 COR-4).
 *
 * Three states, none optimistic:
 *  - resolved      → redirect to the registry landing path
 *  - pending       → neutral boot state (never a /home flash — a banking
 *                    user glimpsing the dataset dashboard before correction
 *                    is a visible defect in front of the customer this
 *                    feature exists for)
 *  - unresolvable / disabled → the degraded neutral shell: an explicit
 *                    unavailability message and sign-out, nothing else.
 *                    There is NO fallback to core_dataset (§5.10).
 *
 * Sign-out is the CANONICAL AuthContext logout (D-099 COR-4): it executes
 * the server /logout request and the full local session cleanup. There is
 * deliberately no second, partial logout path here.
 */

import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useExperienceProfile } from '../../hooks/useExperienceProfile';
import { useAuth } from '../../hooks/useAuth';

export const ProfileLanding: React.FC = () => {
  const profile = useExperienceProfile();
  const { logout } = useAuth();
  const navigate = useNavigate();

  if (profile.status === 'resolved') {
    return <Navigate to={profile.definition.landingPath} replace />;
  }

  if (profile.status === 'pending') {
    return (
      <div
        data-testid="profile-landing-pending"
        className="profile-landing-pending"
        aria-busy="true"
        aria-label="Preparing your workspace"
      />
    );
  }

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  // 'unresolvable' and 'disabled' are handled identically: deny the shell,
  // never substitute another organization's experience.
  return (
    <div data-testid="profile-degraded-shell" className="profile-degraded-shell" role="alert">
      <h1>This workspace is temporarily unavailable — contact support.</h1>
      <p>
        Your organization&apos;s workspace configuration could not be resolved by this
        deployment. Your data is unaffected. Please contact your administrator or
        support.
      </p>
      <button type="button" onClick={() => void handleSignOut()}>
        Sign out
      </button>
    </div>
  );
};

export default ProfileLanding;
